import { AxiosError } from 'axios';
import { forwardRef, useImperativeHandle, useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDataGrid from '../../../components/data-grid';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import Modal from "@mui/material/Modal";
import { deleteIcon, downloadIcon, EditIcon, searchIcon, crossIcon, trashIcon } from '../../../assets/images/index';
import { SearchExerciseService } from "../../../services/search-exercise/search-exercise.service";
import { NewExerciseService } from "../../../services/new-exercises/new-exercises.service";
import { showErrorToast, showSuccessToast } from "../../../components/Toaster/index";
import Dropdown from "../../../components/dropdown/index";
import './exercise-list.scss'
import Button from '../../../components/button';
import { scopeCSS } from "../../../utils/scopeCSS";
import { Link } from "@mui/material";
import ExerciseDetailsDrawer from '../../../components/exercise-details-drawer/ExerciseDetailsDrawer';
import { hebrewLetters } from '../../../configs/config';
import ConfirmationModal from '../../../components/confirmation-modal';
import { useLessonStore } from '../../../utils/lessonStore';


const ExerciseList = forwardRef(({ fetchCounts }: { fetchCounts?: () => void }, ref) => {
    const { t } = useTranslation();
    const exerciseListRef = useRef<any>(null);
    const newExerciseService = new NewExerciseService();
    const searchExerciseService = new SearchExerciseService();

    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [levelOptions, setLevelOptions] = useState([{ value: '', label: t('all') }]);
    const [difficultyOptions, setDifficultyOptions] = useState([{ value: '', label: t('all') }]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedExerciseId, setSelectedExerciseId] = useState<number | null>(null);
    const [filters, setFilters] = useState({ filename: '', status: '', level: '', topic: '', difficulty: '' });
    const [openChartModal, setOpenChartModal] = useState(false);
    const [chartImage, setChartImage] = useState<string | null>(null);    
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [confirmationLoading, setConfirmationLoading] = useState(false);
    const [confirmationData, setConfirmationData] = useState<{
        title?: string;
        message: string;
        onConfirm: () => Promise<void> | void;
    }>({ message: '', onConfirm: () => {} });    

    const {
        topicOptions: storeTopicOptions,
        isTopicsLoading,
        fetchTopics: storeFetchTopics,
    } = useLessonStore();
    
    const toggleDrawer = (open: boolean) => () => {
          setDrawerOpen(open);
          if (!open) setSelectedExerciseId(null);
    };

    const statusOptions = [
        { value: '', label: t('all') },
        { value: 'approved', label: t('approved') },
        { value: 'not_approved', label: t('notApproved') },
    ];

    const handleCloseChart = () => {
        setOpenChartModal(false);
        setChartImage(null);
    };

    const fetchLevels = async () => {
        try {
            const levels = await searchExerciseService.getLevels();
            const formatted = levels.map((l: any) => ({ value: l.id.toString(), label: l.name }));
            setLevelOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch levels", error);
        }
    };

    const fetchTopics = async () => {
        storeFetchTopics(searchExerciseService);
    };

    const topicOptions = useMemo(() => [{ value: '', label: t('all') }, ...storeTopicOptions], [storeTopicOptions, t]);


    const fetchDifficulties = async () => {
        try {
            const difficulties = await searchExerciseService.getDifficulties();
            const formatted = difficulties.map((d: any) => ({ value: d.id.toString(), label: d.name }));
            setDifficultyOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch difficulties", error);
        }
    };


    const handleDownload = async (row: any) => {
        try {
            const response = await newExerciseService.downloadExerciseFromAzure(row.exerciseName, row.id);

            const blob = new Blob([response], {
                type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = row.exerciseName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
        }
    };
    const handleDelete = async (row: any) => {
        try {
            const res = await searchExerciseService.deleteExerciseFromDB(row.id);
            if (res.status === 'success') {
                showSuccessToast(`${t('exerciseDeletedSuccessfully')} ${row.filename}`);
                fetchCounts?.();
                fetchExercises();
            } else {
                showErrorToast(res.message || `${t('errorDeletingExercise')} ${row.filename}`);
            }
        } catch (err) {
            const msg = `${t('errorDeletingExercise')} '${row.filename}'`;
            showErrorToast(msg);
        }
    };
    const fetchExercises = async () => {
        setLoading(true);
        try {
            const data = await searchExerciseService.searchExerciseInDB(
                page + 1,
                pageSize,
                filters.filename,
                filters.status,
                filters.level,
                filters.topic,
                filters.difficulty
            );

            const formatted = data.results.map((item: any) => ({
                id: item.id,
                filename: item.filename.replace(/\.docx$/i, ''),
                status: item.is_exercise_approved && item.is_solution_approved ? t('approved') : t('notApproved'),
                upload_date: new Date(item.upload_date).toLocaleDateString('he-IL'),
                is_solution_approved: item.is_solution_approved,
                exerciseName: item.filename
            }));

            setRows(formatted);
            setRowCount(data.total);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        fetchExercises
    }));

    useEffect(() => {
        fetchExercises();
    }, [page, pageSize]);

    useEffect(() => {
        fetchLevels();
        fetchTopics();
        fetchDifficulties();
    }, []);


    const columns = [
        {
            field: 'filename',
            headerName: t('exerciseName'),
            flex: 3,
            cellClassName: 'exercise-name-ltr'
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 2,
            renderCell: (params: any) => (
                <span className={params.value === 'אושר' ? 'status-approved' : 'status-not-approved'}>
                    {params.value}
                </span>
            )
        },
        {
            field: 'upload_date',
            headerName: t('uploadDate'),
            flex: 2
        },
        {
            field: 'actions',
            headerName: t('operations'),
            sortable: false,
            flex: 1,
            renderCell: (params: any) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                    <IconButton className='action_btns download_btn' onClick={() => {
                        setSelectedExerciseId(params.row.id);
                        setDrawerOpen(true);
                    }}>
                        <img src={EditIcon} alt="Edit" />
                    </IconButton>
                    <IconButton className='action_btns download_btn' onClick={() => handleDownload(params.row)}>
                        <img src={downloadIcon} alt="Download" />
                    </IconButton>
                    <IconButton className='action_btns' 
                      onClick={() => {
                        setConfirmationData({
                          title: t('confirmDeleteExercise'),
                          message: `${t('deleteExerciseConfirmation')} <strong>${params.row.filename}</strong>`,
                          onConfirm: async () => {
                            setConfirmationLoading(true);
                            await handleDelete(params.row);
                            setConfirmationOpen(false);
                            setConfirmationLoading(false);
                          },
                        });
                        setConfirmationOpen(true);
                      }}
                    >
                        <img src={deleteIcon} alt="Delete" />
                    </IconButton>
                </div>
            )
        }
    ];
    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleRemoveFilter = (key: string) => {
        setFilters(prev => ({ ...prev, [key]: '' }));
    };

    const handleClearAllFilters = () => {
        setFilters({ filename: '', status: '', level: '', topic: '', difficulty: '' });
    };

    const getActiveFilters = () => {
        const active: { key: string, label: string, value: string }[] = [];

        if (filters.status) {
            const label = statusOptions.find(opt => opt.value === filters.status)?.label || filters.status;
            active.push({ key: 'status', label: t('status'), value: label });
        }
        if (filters.level) {
            const label = levelOptions.find(opt => opt.value === filters.level)?.label || filters.level;
            active.push({ key: 'level', label: t('level'), value: label });
        }
        if (filters.topic) {
            const label = topicOptions.find(opt => opt.value === filters.topic)?.label || filters.topic;
            active.push({ key: 'topic', label: t('topic'), value: label });
        }
        if (filters.difficulty) {
            const label = difficultyOptions.find(opt => opt.value === filters.difficulty)?.label || filters.difficulty;
            active.push({ key: 'difficulty', label: t('difficulty'), value: label });
        }

        return active;
    };

    const activeFilters = getActiveFilters();

    return (
        <div className='dashboard-table'>
            <Box className='search_exercise'>
                <Dropdown
                    label={t('status')}
                    value={filters.status}
                    options={statusOptions}
                    onChange={(value) => handleFilterChange('status', value.toString())}
                />
                <Dropdown
                    label={t('level')}
                    value={filters.level}
                    options={levelOptions}
                    onChange={(value) => handleFilterChange('level', value.toString())}
                />
                <Dropdown
                    label={t('topic')}
                    value={filters.topic}
                    options={topicOptions}
                    loading={isTopicsLoading}
                    onChange={(value) => handleFilterChange('topic', value.toString())}
                />
                <Dropdown
                    label={t('difficulty')}
                    value={filters.difficulty}
                    options={difficultyOptions}
                    onChange={(value) => handleFilterChange('difficulty', value.toString())}
                />
                <TextField
                    label={t('exerciseNameOrId')}
                    variant="outlined"
                    size="small"
                    value={filters.filename}
                    onChange={(e) => handleFilterChange('filename', e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <img src={searchIcon} alt="Search" />
                            </InputAdornment>
                        )
                    }}
                />
                <Button text={t('search')} color='blue' onClick={fetchExercises} />
            </Box>

            {activeFilters.length > 0 && (
                <div className='table_header'>
                    <p className='filter_result_count'>
                        <span>{rowCount}</span> {t('resultsFound')}
                    </p>
                    <div className='table_header_button'>
                        {activeFilters.map((filter) => (
                            <div className='single_header_button' key={filter.key}>
                                <span>{filter.label}:</span>
                                <ul>
                                    <li>
                                        <span>{filter.value}</span>
                                        <button onClick={() => handleRemoveFilter(filter.key)}>
                                            <img src={crossIcon} alt="Cross Icon" />
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        ))}
                        <button className='delete_filter_btn' onClick={handleClearAllFilters}>
                                <img src={trashIcon} alt="Trash Icon" />
                            <span>{t('deleteFilters')}</span>
                        </button>
                    </div>
                </div>
            )}

            <CustomDataGrid
                rows={rows}
                columns={columns}
                rowCount={rowCount}
                page={page}
                pageSize={pageSize}
                loading={loading}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
            />
            <ExerciseDetailsDrawer 
                open={drawerOpen} 
                onClose={() => setDrawerOpen(false)} 
                exerciseId={selectedExerciseId}
                mode="edit"
                onUpdateSuccess={fetchExercises}
            />
            <Modal open={openChartModal} onClose={handleCloseChart}>
                <Box className="chart-modal-box">
                    <button
                        className="chart-modal-close"
                        onClick={handleCloseChart}
                        aria-label="Close"
                    >
                    ×
                    </button>
                    <img
                        src={
                            chartImage
                            ? chartImage.startsWith("data:")
                                ? chartImage
                                : `data:image/png;base64,${chartImage}`
                            : ""
                        }
                        alt="Chart"
                        className="chart-modal-img"
                    />
                </Box>
            </Modal>
            <ConfirmationModal
                open={confirmationOpen}
                title={confirmationData.title}
                message={confirmationData.message}
                loading={confirmationLoading}
                onConfirm={confirmationData.onConfirm}
                onCancel={() => setConfirmationOpen(false)}
                confirmText={t('yes')}
                cancelText={t('no')}
            />
        </div>
    );
});


export default ExerciseList;
